<template>
  <div class="bg-black sm:hidden">
    <div class="relative flex items-center justify-between px-4 py-2">
      <Logo class="h-11 w-fit" />
      <div
        class="inline-flex h-8 items-center rounded-lg border-2 border-white px-2"
        @click="toggleMobileNav"
      >
        <component :is="Bars3Icon" class="w-5 stroke-2 text-white/80" />
        <span class="ml-2 text-white">{{ t('navigation.menu') }}</span>
      </div>
      <div
        v-if="isMobileNavExpanded"
        class="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-black"
      >
        <div class="flex items-center justify-between px-4 py-2">
          <Logo class="h-11 w-fit" />
          <div
            class="inline-flex h-8 items-center rounded-lg border-2 border-white px-2"
            @click="toggleMobileNav"
          >
            <component :is="XMarkIcon" class="w-5 stroke-2 text-white/80" />
            <span class="ml-2 text-white">{{ t('navigation.close') }}</span>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto px-4">
          <!-- top - org select -->
          <div class="py-2">
            <hr class="border border-gray-600" />
            <div class="relative flex h-20 items-center text-white transition-all duration-300">
              <OrganisationSwitcherMenu
                v-if="loggedInUserOrgList.length > 1"
                v-model:open="orgsMenuOpen"
                :orgs="orgs"
                :selected-org-id="selectedOrgId"
                :show-create-new="showCreateNew"
                :create-new-label="createNewLabel"
                @select-org="handleSelectOrg"
                @set-primary="onSetPrimary"
                @create-new="handleCreateNew"
              >
                <template #trigger>
                  <button
                    type="button"
                    class="flex w-full overflow-hidden"
                    aria-haspopup="menu"
                    :aria-expanded="orgsMenuOpen"
                    data-cy="organisation-switcher-trigger"
                  >
                    <div
                      class="absolute top-1/2 left-1/2 w-full -translate-x-1/2 -translate-y-1/2 transform p-0.5"
                    >
                      <div class="inline-flex w-full items-center px-6">
                        <span class="type-body-1 mr-1 ml-2 line-clamp-1 text-left">
                          {{ orgName }}
                        </span>
                        <ChevronDownIcon class="ml-auto w-4 stroke-2" />
                      </div>
                    </div>
                  </button>
                </template>
              </OrganisationSwitcherMenu>
              <p v-else class="w-full text-center text-ellipsis">{{ orgName }}</p>
            </div>
            <hr class="border border-gray-600" />
          </div>

          <!-- middle - nav items -->
          <div class="grid gap-1">
            <div v-for="(item, index) in filteredNavItems" :key="index" :data-cy="item.label">
              <router-link v-if="item.route" :to="item.route" @click="isMobileNavExpanded = false">
                <div class="flex items-center justify-between p-2 text-white">
                  <div class="flex">
                    <component
                      :is="isItemActive(item) ? item.activeIcon : item.icon"
                      :class="[
                        isItemActive(item) ? 'text-brand-primary-orange' : 'text-white',
                        'h-5 w-5',
                      ]"
                    />
                    <p class="type-body-1 pl-3">{{ item.label }}</p>
                  </div>
                  <!-- Unguarded: the badge renders nothing for a count of nothing. -->
                  <div>
                    <Badge
                      v-if="item.label === t('routes.accounts.accountsPageTitle')"
                      :value="accountsRequestsCount"
                      tone="info"
                      class="ml-auto"
                    />
                    <Badge
                      v-if="item.label === t('routes.orders.ordersPageTitle')"
                      :value="orderAndReturnRequestsCount"
                      tone="info"
                      class="ml-auto"
                    />
                    <Badge
                      v-if="item.label === t('routes.orders.claimsPageTitle')"
                      :value="claimRequestsCount"
                      tone="info"
                      class="ml-auto"
                    />
                    <Badge
                      v-if="item.label === t('routes.clients.navigationLabel')"
                      :value="clientVariationCount"
                      tone="info"
                      class="ml-auto"
                    />
                  </div>
                </div>
              </router-link>
            </div>
          </div>
        </div>

        <!-- bottom - settings and help -->
        <div class="mt-auto px-4 pt-4 pb-5 text-white">
          <NavFeedbackLink variant="mobile" />
          <DropdownMenu
            :items="accountMenuItems"
            size="comfortable"
            @open-change="accountMenuOpen = $event"
            @select="handleAccountMenuSelect"
          >
            <template #trigger>
              <button
                type="button"
                class="flex w-full rounded-md bg-white/20 p-2"
                aria-haspopup="menu"
                :aria-expanded="accountMenuOpen"
              >
                <div class="relative">
                  <Avatar :label="userName" />
                  <div class="absolute -right-1 -bottom-0 rounded-full bg-white p-0.5 text-black">
                    <Cog6ToothIcon class="w-4" />
                  </div>
                </div>
                <div class="ml-2 text-left">
                  <p class="type-body-1 truncate">{{ userFirstName }}</p>
                  <p class="type-body-2 truncate">{{ userEmail }}</p>
                </div>
              </button>
            </template>
          </DropdownMenu>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import router from '@/router';
import { ref, computed } from 'vue';
import i18n from '@/plugins/vue-i18n';
import { Bars3Icon, ChevronDownIcon, Cog6ToothIcon, XMarkIcon } from '@heroicons/vue/24/outline';

import type { NavMenuItem } from '@/types/components/navigation.types';

import { Avatar, Badge, DropdownMenu } from '@boqnet/ui';
import Logo from '@/components/Brand/Logo.vue';
import NavFeedbackLink from '@/components/Navigation/NavFeedbackLink.vue';

import { loggedInUserOrgList } from '@/common/utils/role.utils';
import { useOrganisationSwitcher } from '@/composables/use-organisation-switcher';
import { useAccountMenuItems } from '@/composables/use-account-menu-items';
import OrganisationSwitcherMenu from '@/components/Navigation/OrganisationSwitcher/OrganisationSwitcherMenu.vue';

const props = defineProps<{
  navItems: NavMenuItem[];
  orgName: string;
  userName: string;
  userFirstName: string;
  userEmail: string;
  paymentRequestsCount: number;
  creditsCount?: number;
  orderRequestsCount: number;
  claimRequestsCount: number;
  returnRequestsCount: number;
  clientVariationCount: number;
  logoUrl?: string;
  showSettingsItems: boolean;
}>();

const { t } = i18n.global;

const isMobileNavExpanded = ref(false);

const { items: accountMenuItems } = useAccountMenuItems(computed(() => props.showSettingsItems));
const accountMenuOpen = ref(false);

const orgsMenuOpen = ref(false);

const {
  orgs,
  selectedOrgId,
  showCreateNew,
  createNewLabel,
  onSelectOrg,
  onSetPrimary,
  onCreateNew,
} = useOrganisationSwitcher();

const handleSelectOrg = async (org: Parameters<typeof onSelectOrg>[0]) => {
  await onSelectOrg(org);
  orgsMenuOpen.value = false;
  closeMobileNav();
};

const handleCreateNew = () => {
  onCreateNew();
  orgsMenuOpen.value = false;
  closeMobileNav();
};

const orderAndReturnRequestsCount = computed(
  () => props.orderRequestsCount + props.returnRequestsCount,
);

// Accounts badge = pending payment requests + pending credits (credits only supplied
// for roles whose Accounts surface includes the Credits tab; defaults to 0). (PE-213)
const accountsRequestsCount = computed(
  () => props.paymentRequestsCount + (props.creditsCount ?? 0),
);

const filteredNavItems = computed(() => {
  if (!props.navItems) return [];

  return props.navItems.filter((item) => {
    return typeof item.visible === 'function' ? item.visible() : item.visible !== false;
  });
});

const isCurrentRoute = (navItemRoute: {
  name: string;
  hash?: string;
  params?: Record<string, string | undefined>;
}) => {
  return router.currentRoute.value.name === navItemRoute.name;
};

const isItemActive = (item: NavMenuItem): boolean =>
  isCurrentRoute(item.route) ||
  !!item.alsoActiveOn?.includes(router.currentRoute.value.name as string);

const toggleMobileNav = () => {
  isMobileNavExpanded.value = !isMobileNavExpanded.value;
};

const handleAccountMenuSelect = () => {
  closeMobileNav();
};

const closeMobileNav = () => {
  isMobileNavExpanded.value = false;
};

defineExpose({ closeMobileNav });
</script>
